import { type FC } from 'react';

import { usePlaceholderContent } from '@sitecore/common';
import { OfferStepRendering } from '@sitecore/types/OfferStep';

import { BaseOfferStep } from './BaseOfferStep';
import { DefaultOfferCard } from './DefaultOfferCard';
import { useOfferStepLogic } from './useOfferStepLogic';

export const OfferStepExtensionFlow: FC = () => {
  const { OfferStep: { fields: textualData, params: { allowDynamicOrHybridOnly = 'false' } = {} } = {} } =
    usePlaceholderContent<{ OfferStep: OfferStepRendering }>();

  const logic = useOfferStepLogic({
    offerMachineEventType: 'GET_EXTENSION_OFFER',
    offerMachinePayload: {
      allowDynamicOrHybridOnly: allowDynamicOrHybridOnly === 'true',
    },

    // No TOU experiment filtering — uses getOffers() directly
    getOffersConfig: null,

    // Conditionally hide product toggle when allowDynamicOrHybridOnly is enabled
    showProductToggleOverride: defaultValue => allowDynamicOrHybridOnly === 'false' && defaultValue,

    // Use Sitecore-driven validation message
    validationMessage: textualData?.content?.noOfferSelectedNotification?.value?.content ?? '',

    // Auto-reselect discount code when product filter changes
    autoReselectOnOffersChange: true,

    // Scroll behavior: uses useEffect with 'nearest' block
    scrollBehavior: 'effect',
    scrollBlock: 'nearest',
  });

  return (
    <BaseOfferStep
      logic={logic}
      consumptionLayout="footer"
      hasChatActivator={false}
      renderOfferCard={offer => (
        <DefaultOfferCard
          key={offer.type}
          isOpen={logic.isPriceDetailsOpen}
          setOpen={logic.setIsPriceDetailsOpen}
          isSelected={offer.type === logic.selectedDiscountCode}
          onSelect={() => {
            if (!offer.type) return;
            logic.setValue('discountCode', offer.type, { shouldValidate: true, shouldDirty: true });
          }}
          offer={offer}
          textualData={logic.textualData}
          flowContext={logic.flowContext}
          energyType={logic.energyType}
        />
      )}
    />
  );
};
